import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Truck,
  Package,
  Clock,
  Shield,
  MapPin,
  Phone,
  Mail,
  Star,
  CheckCircle,
  ArrowRight,
  Globe,
  Users,
  BarChart3,
  Plus,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  calculateAirShippingCost,
  calculateSeaShippingCost,
  pricingTierService,
  settingsService,
} from "@/lib/database";
import { PricingTier } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { nanoid } from "nanoid";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type QuotePackage = {
  id: string;
  length: string;
  width: string;
  height: string;
  scaleWeight: string;
};

const createEmptyQuotePackage = (): QuotePackage => ({
  id: nanoid(),
  length: "",
  width: "",
  height: "",
  scaleWeight: "",
});

const computeDimensionalWeight = (
  pkg: QuotePackage,
  dimensionUnit: "inches" | "cm"
) => {
  const length = parseFloat(pkg.length);
  const width = parseFloat(pkg.width);
  const height = parseFloat(pkg.height);

  if (!length || !width || !height) return null;

  const lengthInInches = dimensionUnit === "cm" ? length / 2.54 : length;
  const widthInInches = dimensionUnit === "cm" ? width / 2.54 : width;
  const heightInInches = dimensionUnit === "cm" ? height / 2.54 : height;

  const dimensionalWeight =
    (lengthInInches * widthInInches * heightInInches) / 166;

  return Number(dimensionalWeight.toFixed(2));
};

const getPackageCostWeight = (
  pkg: QuotePackage,
  dimensionUnit: "inches" | "cm",
  weightUnit: "lbs" | "kg"
) => {
  const dimensionalWeight = computeDimensionalWeight(pkg, dimensionUnit) || 0;

  const scaleWeightLbs =
    weightUnit === "kg"
      ? (parseFloat(pkg.scaleWeight) || 0) * 2.20462
      : parseFloat(pkg.scaleWeight) || 0;

  return Math.max(dimensionalWeight, scaleWeightLbs);
};

export default function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({
    shippingMethod: "air" as "air" | "sea",
    weightUnit: "lbs" as "lbs" | "kg",
    makeExpress: false,
    dimensionUnit: "inches" as "inches" | "cm",

    // Common fields
    includePickup: false,
    includeDelivery: false,
  });
  const [quoteResult, setQuoteResult] = useState<number | null>(null);
  const [quoteRange, setQuoteRange] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [pricingSettings, setPricingSettings] = useState({
    seaRate: 0,
    deliveryFeeMin: 0,
    deliveryFeeMax: 0,
    pickupFeeMin: 0,
    pickupFeeMax: 0,
    expressFee: 0,
  });
  const [estimationSettings, setEstimationSettings] = useState<
    Record<string, number>
  >({});
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [activeTab, setActiveTab] = useState("quote-tool");
  const [quotePackages, setQuotePackages] = useState<QuotePackage[]>([
    createEmptyQuotePackage(),
  ]);

  const loadPricingSettings = async () => {
    try {
      const settings = await settingsService.getPricingSettings();
      setPricingSettings(settings);
      const estimationSettingsData =
        await settingsService.getEstimationSettings();
      setEstimationSettings(estimationSettingsData);

      const tiers: PricingTier[] = await pricingTierService.getAll();

      setPricingTiers(tiers);
    } catch (error) {
      console.error("Error loading pricing settings:", error);
    }
  };

  const services = [
    {
      icon: <Truck className="h-8 w-8" />,
      title: "Express Delivery",
      description: "Fast air freight delivery between US and Haiti",
      features: [
        "As fast as 3 days delivery",
        "Real-time tracking",
        "Priority handling",
      ],
    },
    {
      icon: <Package className="h-8 w-8" />,
      title: "Sea Freight Services",
      description: "Cost-effective sea freight between US and Haiti",
      features: [
        "Up to 4 weeks delivery",
        "Bulk shipping",
        "Competitive rates",
      ],
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Bidirectional Shipping",
      description:
        "Specialized shipping between US and Haiti with customs expertise",
      features: ["Customs clearance", "Door-to-door service", "Local delivery"],
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Secure Transport",
      description: "Safe and reliable handling of your valuable items",
      features: [
        "Secure packaging",
        "Chain of custody",
        "Professional handling",
      ],
    },
  ];

  const stats = [
    {
      icon: <Package className="h-6 w-6" />,
      value: "50K+",
      label: "Packages Delivered",
    },
    {
      icon: <Users className="h-6 w-6" />,
      value: "1,200+",
      label: "Happy Customers",
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      value: "25+",
      label: "Cities Covered",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      value: "99.8%",
      label: "On-Time Delivery",
    },
  ];

  const testimonials = [
    {
      name: "Marie Jean-Baptiste",
      company: "Regular Customer",
      content:
        "108 Shipping has made sending packages to my family in Haiti so easy and reliable. The tracking system gives me peace of mind.",
      rating: 5,
    },
    {
      name: "Pierre Moïse",
      company: "Business Owner",
      content:
        "Their sea freight service has helped my import business grow. Professional handling and competitive rates.",
      rating: 5,
    },
    {
      name: "Claudette Augustin",
      company: "Frequent Shipper",
      content:
        "Fast, secure, and affordable. I've been using 108 Shipping for over two years and never had an issue.",
      rating: 5,
    },
  ];

  const addQuotePackage = () => {
    setQuotePackages((prev) => [...prev, createEmptyQuotePackage()]);
  };

  const removeQuotePackage = (id: string) => {
    setQuotePackages((prev) =>
      prev.length > 1 ? prev.filter((pkg) => pkg.id !== id) : prev
    );
  };

  const updateQuotePackageField = (
    id: string,
    fields: Partial<QuotePackage>
  ) => {
    setQuotePackages((prev) =>
      prev.map((pkg) => (pkg.id === id ? { ...pkg, ...fields } : pkg))
    );
  };

  const calculateQuote = async () => {
    setQuoteResult(null);
    setQuoteRange(null);

    try {
      let basePrice = 0;

      if (quoteData.shippingMethod === "air") {
        const totalWeight = quotePackages.reduce(
          (sum, pkg) =>
            sum +
            getPackageCostWeight(
              pkg,
              quoteData.dimensionUnit,
              quoteData.weightUnit
            ),
          0
        );

        if (!totalWeight || totalWeight <= 0) {
          setQuoteResult(0);
          setQuoteRange(null);
          return;
        }

        // Get air rate for this weight from pricing tiers
        const airRate =
          pricingTiers.find((tier) => tier.tier_order === 1)?.rate || 0;
        const weightTier = pricingTiers.find(
          (tier) =>
            totalWeight >= tier.min_weight &&
            (!tier.max_weight || totalWeight <= tier.max_weight)
        );

        if (weightTier) {
          basePrice = calculateAirShippingCost(totalWeight, weightTier.rate);
          if (quoteData.makeExpress) {
            basePrice += pricingSettings.expressFee;
          }
        } else {
          basePrice = calculateAirShippingCost(totalWeight, airRate);
          if (quoteData.makeExpress) {
            basePrice += pricingSettings.expressFee;
          }
        }
      } else {
        const seaCost = quotePackages.reduce((sum, pkg) => {
          let length = parseFloat(pkg.length);
          let width = parseFloat(pkg.width);
          let height = parseFloat(pkg.height);

          if (
            !length ||
            !width ||
            !height ||
            length <= 0 ||
            width <= 0 ||
            height <= 0
          ) {
            return sum;
          }

          // Convert cm to inches if needed
          if (quoteData.dimensionUnit === "cm") {
            length = length / 2.54;
            width = width / 2.54;
            height = height / 2.54;
          }

          return (
            sum +
            calculateSeaShippingCost(
              length,
              width,
              height,
              pricingSettings.seaRate,
              false,
              0
            )
          );
        }, 0);

        basePrice = seaCost;
      }

      if (quoteData.includeDelivery || quoteData.includePickup) {
        // Show range when pickup and/or delivery is requested
        let minPrice = basePrice;
        let maxPrice = basePrice;

        if (quoteData.includePickup) {
          minPrice += pricingSettings.pickupFeeMin;
          maxPrice += pricingSettings.pickupFeeMax;
        }

        if (quoteData.includeDelivery) {
          minPrice += pricingSettings.deliveryFeeMin;
          maxPrice += pricingSettings.deliveryFeeMax;
        }

        setQuoteRange({
          min: minPrice,
          max: maxPrice,
        });
      } else {
        setQuoteResult(basePrice);
      }
    } catch (error) {
      console.error("Error calculating quote:", error);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    calculateQuote();
  }, [
    quoteData.includeDelivery,
    quoteData.includePickup,
    quoteData.weightUnit,
    quoteData.dimensionUnit,
    quotePackages,
    quoteData.shippingMethod,
    quoteData.makeExpress,
  ]);

  useEffect(() => {
    loadPricingSettings();
  }, []);

  return (
    <div id="hero" className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link onClick={() => scrollToSection("hero")} to="#">
                <img
                  src="/108shippingservices-logo-trans.png"
                  alt="108 Shipping & Services"
                  className="w-40 h-40"
                />
              </Link>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="#services"
                className="text-gray-700 hover:text-[#254164] transition-colors"
              >
                Services
              </a>
              <button
                onClick={() => scrollToSection("why-choose-us")}
                className="text-gray-700 hover:text-[#254164] transition-colors"
              >
                Why Us
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-gray-700 hover:text-[#254164] transition-colors"
              >
                FAQ
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-gray-700 hover:text-[#254164] transition-colors"
              >
                Contact
              </button>
              {/* Show Dashboard if logged in, Login if not */}
              {localStorage.getItem("userRole") ? (
                <Link
                  to={
                    localStorage.getItem("userRole") === "admin"
                      ? "/admin/dashboard"
                      : "/dashboard"
                  }
                >
                  <Button
                    variant="outline"
                    className="border-[#254164] text-[#254164] hover:bg-[#254164] hover:text-white"
                  >
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth/login">
                  <Button
                    variant="outline"
                    className="border-[#254164] text-[#254164] hover:bg-[#254164] hover:text-white"
                  >
                    Login
                  </Button>
                </Link>
              )}
            </nav>

            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span
                  className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                    isMenuOpen ? "rotate-45 translate-y-1" : "-translate-y-0.5"
                  }`}
                ></span>
                <span
                  className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${
                    isMenuOpen ? "opacity-0" : "opacity-100"
                  }`}
                ></span>
                <span
                  className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                    isMenuOpen ? "-rotate-45 -translate-y-1" : "translate-y-0.5"
                  }`}
                ></span>
              </div>
            </button>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a
                  href="#services"
                  className="text-gray-700 hover:text-[#254164] transition-colors"
                >
                  Services
                </a>
                <button
                  onClick={() => scrollToSection("why-choose-us")}
                  className="text-gray-700 hover:text-[#254164] transition-colors text-left"
                >
                  Why Us
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="text-gray-700 hover:text-[#254164] transition-colors text-left"
                >
                  Contact
                </button>
                <Link to="/auth/login">
                  <Button
                    variant="outline"
                    className="border-[#254164] text-[#254164] hover:bg-[#254164] hover:text-white w-full"
                  >
                    Login
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#254164] to-[#1a2d47] text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white border-white/30 hover:bg-white/30">
              Your Trusted Logistics Partner
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Your Shipment,
              <span className="text-[#d3d9e0]"> Our Priority</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-[#d3d9e0] leading-relaxed">
              {/* Professional shipping services between the US and Haiti with real-time tracking,
              secure handling, and on-time delivery guaranteed. */}
              {/* Ship to Haiti with peace of mind with us! */}
              Ship packages between the US and Haiti safely and affordably.
              Track your shipment every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/auth/login">
                <Button
                  size="lg"
                  className="
                  bg-white
                  text-[#254164]
                  hover:bg-[#d3d9e0]
                  text-lg
                  px-8
                  py-3
                  w-[260px]
                  md:w-auto
                  "
                >
                  Track Your Shipment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>

              <Button
                size="lg"
                className="
                bg-white
                text-[#254164]
                hover:bg-[#d3d9e0]
                text-lg
                px-8
                py-3
                w-[260px]
                md:w-auto
                "
                onClick={() => scrollToSection("quote-section")}
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#d3d9e0]/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-[#254164] text-white rounded-full">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-3xl font-bold text-[#254164] mb-1">
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#254164]/10 text-[#254164] border-[#254164]/20">
              Our Services
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Shipping Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From air freight to cost-effective sea freight services, we
              provide reliable shipping solutions between the US and Haiti.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-[#254164]/30"
              >
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 p-3 bg-[#254164]/10 text-[#254164] rounded-full w-fit group-hover:bg-[#254164] group-hover:text-white transition-colors">
                    {service.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-900">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center text-sm text-gray-600"
                      >
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="why-choose-us" className="py-20 bg-[#d3d9e0]/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-[#254164]/10 text-[#254164] border-[#254164]/20">
                Why Choose Us
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Reliable, Secure, and Efficient Logistics
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                With years of experience in bidirectional US-Haiti shipping, we
                understand the importance of timely and secure delivery. Our
                advanced tracking systems and professional team ensure your
                packages reach their destination safely.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-[#254164] text-white rounded-lg">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Real-Time Tracking
                    </h3>
                    <p className="text-gray-600">
                      Monitor your shipments 24/7 with our advanced tracking
                      system.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-[#254164] text-white rounded-lg">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Secure Handling
                    </h3>
                    <p className="text-gray-600">
                      Your packages are handled with the utmost care and
                      security.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-[#254164] text-white rounded-lg">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Haiti Coverage
                    </h3>
                    <p className="text-gray-600">
                      Extensive network covering Port-au-Prince and major cities
                      in Haiti.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabbed Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="quote-tool">Quote Estimation</TabsTrigger>
                <TabsTrigger value="pricing-list">Pricing List</TabsTrigger>
              </TabsList>

              <TabsContent value="quote-tool">
                <div className="relative" id="quote-section">
                  <div className="bg-gradient-to-br from-[#254164] to-[#1a2d47] rounded-2xl p-8 text-white">
                    <h3 className="text-2xl font-bold mb-6">
                      Get Instant Quote
                    </h3>
                    <div className="space-y-6">
                      {/* Shipping Method Selection */}
                      <div>
                        <Label className="text-sm text-[#d3d9e0] mb-3 block">
                          Shipping Method
                        </Label>
                        <Select
                          value={quoteData.shippingMethod}
                          onValueChange={(value: "air" | "sea") =>
                            setQuoteData({
                              ...quoteData,
                              shippingMethod: value,
                            })
                          }
                        >
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="air">
                              Air Shipping - Fast Delivery (
                              {estimationSettings.air_shipping_min_transit_days}
                              -
                              {estimationSettings.air_shipping_max_transit_days}{" "}
                              days)
                            </SelectItem>
                            <SelectItem value="sea">
                              Sea Shipping - Economical (
                              {estimationSettings.sea_shipping_min_transit_days}
                              -
                              {estimationSettings.sea_shipping_max_transit_days}{" "}
                              days)
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        {/* Express Air Shipping Option */}
                        {quoteData.shippingMethod === "air" && (
                          <div className="space-x-2 flex items-center mt-2">
                            <input
                              type="checkbox"
                              id="express"
                              className="rounded border-white/20"
                              checked={quoteData.makeExpress}
                              onChange={(e) =>
                                setQuoteData({
                                  ...quoteData,
                                  makeExpress: e.target.checked,
                                })
                              }
                            />
                            <Label
                              htmlFor="express"
                              className="text-sm cursor-pointer"
                            >
                              Express Air Shipping (+$
                              {pricingSettings.expressFee}) - Fastest Delivery (
                              {
                                estimationSettings.express_shipping_min_transit_days
                              }
                              -
                              {
                                estimationSettings.express_shipping_max_transit_days
                              }{" "}
                              days)
                            </Label>
                          </div>
                        )}
                      </div>

                      {/* Pickup Option */}
                      <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                        <input
                          type="checkbox"
                          id="pickup"
                          className="rounded border-white/20"
                          checked={quoteData.includePickup}
                          onChange={(e) =>
                            setQuoteData({
                              ...quoteData,
                              includePickup: e.target.checked,
                            })
                          }
                        />
                        <Label
                          htmlFor="pickup"
                          className="text-sm cursor-pointer"
                        >
                          Sender needs home pickup (+$
                          {pricingSettings.pickupFeeMin}-$
                          {pricingSettings.pickupFeeMax})
                        </Label>
                      </div>

                      {/* Unit Selection */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {quoteData.shippingMethod === "air" && (
                          <div>
                            <Label className="text-sm text-[#d3d9e0] mb-3 block">
                              Weight Unit
                            </Label>
                            <div className="flex items-center space-x-4 p-3 bg-white/10 rounded-lg">
                              <Label htmlFor="weight-unit" className="text-sm">
                                {quoteData.weightUnit === "lbs"
                                  ? "Imperial (lbs)"
                                  : "Metric (kg)"}
                              </Label>
                              <Switch
                                id="weight-unit"
                                checked={quoteData.weightUnit === "kg"}
                                onCheckedChange={(checked) =>
                                  setQuoteData({
                                    ...quoteData,
                                    weightUnit: checked ? "kg" : "lbs",
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}

                        <div>
                          <Label className="text-sm text-[#d3d9e0] mb-3 block">
                            Dimension Unit
                          </Label>
                          <div className="flex items-center space-x-4 p-3 bg-white/10 rounded-lg">
                            <Label htmlFor="dimension-unit" className="text-sm">
                              {quoteData.dimensionUnit === "inches"
                                ? "Imperial (inches)"
                                : "Metric (cm)"}
                            </Label>
                            <Switch
                              id="dimension-unit"
                              checked={quoteData.dimensionUnit === "cm"}
                              onCheckedChange={(checked) =>
                                setQuoteData({
                                  ...quoteData,
                                  dimensionUnit: checked ? "cm" : "inches",
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* Packages */}
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <Label className="text-sm text-[#d3d9e0]">
                              Packages
                            </Label>
                            <p className="text-xs text-[#d3d9e0]/80">
                              Add multiple packages to estimate the combined
                              cost. We automatically use the heaviest weight for
                              each package.
                            </p>
                          </div>
                          <Button
                            variant="secondary"
                            className="bg-white text-[#254164] hover:bg-[#d3d9e0]"
                            onClick={addQuotePackage}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Package
                          </Button>
                        </div>

                        <div className="flex gap-4 overflow-x-auto pb-2">
                          {quotePackages.map((pkg, index) => {
                            const dimensionalWeight = computeDimensionalWeight(
                              pkg,
                              quoteData.dimensionUnit
                            );
                            const packageCostWeight = getPackageCostWeight(
                              pkg,
                              quoteData.dimensionUnit,
                              quoteData.weightUnit
                            );
                            const hasWeight =
                              (dimensionalWeight ?? 0) > 0 ||
                              (parseFloat(pkg.scaleWeight) || 0) > 0;

                            return (
                              <div
                                key={pkg.id}
                                className="relative min-w-[260px] max-w-[260px] aspect-square rounded-xl bg-white/10 border border-white/20 p-4 flex flex-col space-y-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-[#d3d9e0]/80">
                                      Package
                                    </p>
                                    <p className="font-semibold">
                                      #{index + 1}
                                    </p>
                                  </div>
                                  {quotePackages.length > 1 && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="text-white hover:bg-white/10"
                                      onClick={() => removeQuotePackage(pkg.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>

                                <div className="grid grid-cols-3 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-[#d3d9e0]">
                                      Length ({quoteData.dimensionUnit})
                                    </Label>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder="0"
                                      value={pkg.length}
                                      onChange={(e) =>
                                        updateQuotePackageField(pkg.id, {
                                          length: e.target.value,
                                        })
                                      }
                                      className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-[#d3d9e0]">
                                      Width ({quoteData.dimensionUnit})
                                    </Label>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder="0"
                                      value={pkg.width}
                                      onChange={(e) =>
                                        updateQuotePackageField(pkg.id, {
                                          width: e.target.value,
                                        })
                                      }
                                      className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-[#d3d9e0]">
                                      Height ({quoteData.dimensionUnit})
                                    </Label>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder="0"
                                      value={pkg.height}
                                      onChange={(e) =>
                                        updateQuotePackageField(pkg.id, {
                                          height: e.target.value,
                                        })
                                      }
                                      className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                                    />
                                  </div>

                                  {quoteData.shippingMethod === "air" && (
                                    <div className="space-y-1 col-span-3">
                                      <Label className="text-xs text-[#d3d9e0]">
                                        Scale Weight ({quoteData.weightUnit})
                                      </Label>
                                      <Input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={pkg.scaleWeight}
                                        onChange={(e) =>
                                          updateQuotePackageField(pkg.id, {
                                            scaleWeight: e.target.value,
                                          })
                                        }
                                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="text-xs text-[#d3d9e0] space-y-1">
                                  <p>
                                    <span className="font-semibold text-white">
                                      Volume:
                                    </span>{" "}
                                    {pkg.length && pkg.width && pkg.height
                                      ? `${
                                          (parseFloat(pkg.length) || 0) *
                                          (parseFloat(pkg.width) || 0) *
                                          (parseFloat(pkg.height) || 0)
                                        } ${
                                          quoteData.dimensionUnit === "cm"
                                            ? "cm³"
                                            : "in³"
                                        }`
                                      : "Enter dimensions"}
                                  </p>
                                  {quoteData.shippingMethod === "air" && (
                                    <>
                                      <p>
                                        <span className="font-semibold text-white">
                                          Volume Weight:
                                        </span>{" "}
                                        {dimensionalWeight
                                          ? `${dimensionalWeight} lbs`
                                          : "Enter dimensions"}
                                      </p>
                                      <p>
                                        <span className="font-semibold text-white">
                                          Weight used for cost:
                                        </span>{" "}
                                        {hasWeight
                                          ? `${packageCostWeight.toFixed(
                                              2
                                            )} lbs (heaviest)`
                                          : "Add weight to calculate"}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Home Delivery Option */}
                      <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                        <input
                          type="checkbox"
                          id="delivery"
                          checked={quoteData.includeDelivery}
                          onChange={(e) =>
                            setQuoteData({
                              ...quoteData,
                              includeDelivery: e.target.checked,
                            })
                          }
                          className="rounded border-white/20"
                        />
                        <Label
                          htmlFor="delivery"
                          className="text-sm cursor-pointer"
                        >
                          Recipient needs home delivery (+$
                          {pricingSettings.deliveryFeeMin}-$
                          {pricingSettings.deliveryFeeMax})
                        </Label>
                      </div>

                      <Button
                        className="w-full bg-white text-[#254164] hover:bg-[#d3d9e0]"
                        onClick={calculateQuote}
                      >
                        Calculate Shipping Cost
                      </Button>

                      {!!quoteResult && (
                        <div className="mt-4 p-4 bg-white/10 rounded-lg">
                          <p className="text-lg font-semibold">
                            Estimated Cost: ${quoteResult.toFixed(2)}
                          </p>
                          <p className="text-sm text-[#d3d9e0] mt-1">
                            Price estimate - final cost may vary
                          </p>
                        </div>
                      )}

                      {!!quoteRange && (
                        <div className="mt-4 p-4 bg-white/10 rounded-lg">
                          <p className="text-lg font-semibold">
                            Estimated Cost: ${quoteRange.min.toFixed(2)} - $
                            {quoteRange.max.toFixed(2)}
                          </p>
                          <p className="text-sm text-[#d3d9e0] mt-1">
                            Price range estimate includes{" "}
                            {quoteData.includePickup &&
                            quoteData.includeDelivery
                              ? "pickup and delivery fees"
                              : quoteData.includePickup
                              ? "pickup fees"
                              : "delivery fees"}
                            . Final cost may vary based on location
                          </p>
                        </div>
                      )}

                      {!quoteResult && !quoteRange && (
                        <p className="text-sm text-[#d3d9e0] italic mt-4">
                          Enter package details above to get an instant quote
                          estimation
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing-list">
                <div className="relative" id="pricing-list-section">
                  <div className="border border-gray-200 hover:border-[#254164]/30 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold mb-6">Our Pricings</h3>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold">
                          Pickup and Delivery Rates
                        </h3>

                        <Table>
                          <TableCaption>
                            Pickup fees vary based on the sender's location, and
                            delivery fees vary based on the recipient's
                            location.
                          </TableCaption>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[100px]"></TableHead>
                              <TableHead>Min. Fee</TableHead>
                              <TableHead>Max. Fee</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="font-medium">
                                Pickup
                              </TableCell>
                              <TableCell>
                                ${pricingSettings.pickupFeeMin}
                              </TableCell>
                              <TableCell>
                                ${pricingSettings.pickupFeeMax}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell className="font-medium">
                                Delivery
                              </TableCell>
                              <TableCell>
                                ${pricingSettings.deliveryFeeMin}
                              </TableCell>
                              <TableCell>
                                ${pricingSettings.deliveryFeeMax}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-xl font-bold">Sea Shipping Rate</h3>

                        <p className="text-sm">
                          Enjoy a simple, sea freight shipping rate of{" "}
                          <span className="font-medium">
                            ${pricingSettings.seaRate} per cubic foot.
                          </span>
                        </p>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-xl font-bold">
                          Air Shipping Rates
                        </h3>
                        <Table>
                          <TableCaption>
                            All prices are subject to change.
                          </TableCaption>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[100px]">
                                Min. Weight
                              </TableHead>
                              <TableHead>Max. Weight</TableHead>
                              <TableHead className="text-right">
                                Rate ($/lb)
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pricingTiers.map((tier) => (
                              <TableRow key={tier.id}>
                                <TableCell>{tier.min_weight} lbs</TableCell>
                                <TableCell>
                                  {tier.max_weight ?? "∞​​"} lbs
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  ${tier.rate}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#254164]/10 text-[#254164] border-[#254164]/20">
              Testimonials
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our satisfied
              customers have to say about our services.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="border-gray-200 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-5 w-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {testimonial.company}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="faq"
        className="py-20 bg-gradient-to-r from-[#254164] to-[#1a2d47] text-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* FAQ Section */}
          <div className="mt-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>

            <p className="text-xl text-[#d3d9e0] mb-8 max-w-2xl mx-auto">
              Find quick answers to the most common questions about our product
              and services.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  How long does delivery take?
                </h4>
                <p className="text-gray-600 text-sm">
                  Air shipping takes{" "}
                  {estimationSettings.air_shipping_min_transit_days}-
                  {estimationSettings.air_shipping_max_transit_days} days or{" "}
                  {estimationSettings.express_shipping_min_transit_days}-
                  {estimationSettings.express_shipping_max_transit_days} days
                  for an additional fee of ${pricingSettings.expressFee}{" "}
                  (Express), while sea shipping takes{" "}
                  {estimationSettings.sea_shipping_min_transit_days}-
                  {estimationSettings.sea_shipping_max_transit_days} days.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Do you offer pickup services?
                </h4>
                <p className="text-gray-600 text-sm">
                  Yes, we offer pickup services from the sender's location for
                  an additional fee of ${pricingSettings.pickupFeeMin}-$
                  {pricingSettings.pickupFeeMax} depending on location.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  How do I place an order?
                </h4>
                <p className="text-gray-600 text-sm">
                  Create an account (COMING SOON - Reach out to us in the
                  meantime via Whatsapp), provide package details, and we'll
                  handle the rest. You can track your shipment in real-time.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  How is pricing calculated?
                </h4>
                <p className="text-gray-600 text-sm">
                  Air shipping is priced by weight, sea shipping by volume
                  (cubic feet). Additional fees apply for pickup and delivery.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Can you ship from Haiti to USA?
                </h4>
                <p className="text-gray-600 text-sm">
                  Yes, we provide bidirectional shipping services between the US
                  and Haiti with the same reliable tracking and handling.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Do you offer home delivery in Haiti?
                </h4>
                <p className="text-gray-600 text-sm">
                  Yes, we offer home delivery throughout Haiti for $
                  {pricingSettings.deliveryFeeMin}-$
                  {pricingSettings.deliveryFeeMax} depending on the location and
                  accessibility.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-2xl md:text-4xl font-bold mb-4 mt-20">
            Ready to Ship with Confidence?
          </h3>
          <p className="text-xl text-[#d3d9e0] mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust 108 Shipping &
            Services for their bidirectional US-Haiti shipping needs. Get
            started today!
          </p>

          <div className="flex justify-center">
            <Button
              size="lg"
              variant="outline"
              className="bg-white text-[#254164] hover:bg-[#d3d9e0] text-lg px-8 py-3"
              onClick={() => scrollToSection("contact")}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#d3d9e0]/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#254164]/10 text-[#254164] border-[#254164]/20">
              Contact Us
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have questions about our services? Our team is here to help you
              find the perfect shipping solution.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="mx-auto mb-4 p-3 bg-[#254164]/10 text-[#254164] rounded-full w-fit">
                  <Phone className="h-6 w-6" />
                </div>

                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Call Us
                </h3>

                <div className="flex justify-center mb-3">
                  <span className="font-medium">Haiti:</span>
                  <p className="text-gray-600">+509 3351-5536</p>
                </div>

                <div className="flex justify-center">
                  <span className="font-medium">US:</span>
                  <p className="text-gray-600">+1 (305) 606-8663</p>
                </div>

                <p className="text-sm text-gray-500 mt-1">Mon-Fri 8AM-6PM</p>
              </CardContent>
            </Card>

            <Card className="text-center border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="mx-auto mb-4 p-3 bg-[#254164]/10 text-[#254164] rounded-full w-fit">
                  <Mail className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Email Us
                </h3>
                <p className="text-gray-600">support@108shipping.com</p>
                <p className="text-sm text-gray-500 mt-1">24/7 Support</p>
              </CardContent>
            </Card>

            <Card className="text-center border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="mx-auto mb-4 p-3 bg-[#254164]/10 text-[#254164] rounded-full w-fit">
                  <MapPin className="h-6 w-6" />
                </div>

                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Visit Us
                </h3>

                <div className="flex flex-col items-center mb-3">
                  <span className="font-medium">Haiti Office:</span>
                  <p className="text-gray-600">
                    108 Rue Lambert, Petion-Ville, Haiti
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <span className="font-medium">US Warehouse:</span>
                  <p className="text-gray-600">
                    7901 NW 67th St, Medley, FL 33166
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#254164] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* <div className="flex items-center justify-center space-x-3 mb-4">
              <Link
                onClick={() => scrollToSection('hero')}
                to="#"
              >
                <img
                  src="/108shippingservices-logo-filled-rect.jpg"
                  alt="108 Shipping & Services"
                  className="w-40 h-20"
                />
              </Link>
            </div> */}
            <p className="text-sm text-[#d3d9e0]">
              &copy; 2025 108 Shipping & Services. <br /> All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
